import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';
import { YUP_SCHEMA } from '@repo/constants';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import '../../../foundations/i18n/i18n';
// 배럴(primitives) 경유 시 overlay↔primitives 순환으로 jsdom 에서 undefined — 직접 경로로 순환 차단
import InputFormikWithLimit from '../../../primitives/TextField/InputFormikWithLimit';

interface VerifiedModalProps {
  email?: string;
  subTitle?: string;
  isVerified: boolean;
  isRefetchMyProfile?: boolean;
  onClickSubmit: (email: string) => Promise<void>;
  closeModal: () => void;
  onControlError: (error: unknown, formik?: FormikProps<{ email: string | undefined }>) => void;
}

export default function EmailVerifyBody({
  email,
  subTitle,
  isVerified,
  onClickSubmit,
  closeModal,
  onControlError,
}: VerifiedModalProps) {
  const { t } = useTranslation();
  const FIRST_VERIFY = !isVerified;

  const emailFormik = useFormik({
    initialValues: {
      email: !isVerified ? email : '',
    },
    validationSchema: Yup.object({
      email: YUP_SCHEMA.EMAIL,
    }),
    onSubmit: async (values) => {
      if (!values.email) return;

      const email = values.email.toLowerCase();
      await onClickSubmit(email).catch((err) => {
        onControlError(err.response?.data, emailFormik);
      });
    },
  });

  return (
    <SWrapper>
      <STitleWrapper>
        <STitleContainer>
          <STitle>
            {FIRST_VERIFY ? t('setting.edit_email_verify_message') : t('setting.edit_email_re_verify_message')}
          </STitle>
        </STitleContainer>
        {subTitle && <SSubTitleContainer>{t(subTitle)}</SSubTitleContainer>}
      </STitleWrapper>
      <SInputBox>
        <SUserEmail
          formik={emailFormik}
          name="email"
          label={''}
          placeholder={isVerified ? email : t('setting.edit_email_verify_placeholder')}
          $isError={!!emailFormik.errors.email}
          autoComplete="off"
        />
        <SValidationSentence $isError={Boolean(emailFormik.touched.email && emailFormik.errors.email)}>
          {t(emailFormik.errors.email ?? '')}
        </SValidationSentence>
      </SInputBox>

      <SWebModalButtonContainer>
        <button type="button" onClick={closeModal}>
          {t('general.cancel')}
        </button>
        <button
          type="button"
          disabled={!emailFormik.values?.email || !emailFormik?.isValid}
          onClick={() => emailFormik.values?.email && emailFormik?.handleSubmit()}
        >
          {t('setting.edit_email_verify_verify_now_button')}
        </button>
      </SWebModalButtonContainer>
    </SWrapper>
  );
}

const SInputBox = styled.div`
  width: 100%;
  max-width: calc(400px - 80px);
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const STitleWrapper = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  position: relative;
`;

const STitleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const STitle = styled.p`
  ${({ theme }) => theme.font.head5_sb};

  @media ${({ theme }) => theme.device.medium} {
    ${({ theme }) => theme.font.body1_sb};
  }
`;

const SSubTitleContainer = styled.div`
  color: ${({ theme }) => theme.color.text.TXT_2};
  ${({ theme }) => theme.font.body2_r};
`;

const SWrapper = styled.div`
  padding: 20px 24px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.color.popup.background};
`;

type SUserEmailProps = {
  $isError: boolean;
};
const SUserEmail = styled(InputFormikWithLimit)<SUserEmailProps>`
  width: 100%;

  input {
    border-radius: 8px;
    position: relative;
    padding: 13px 12px;
    height: 48px;
    ${({ theme }) => theme.font.body2_r};
    color: ${({ theme }) => theme.color.text.TXT_3};
    background-color: ${({ theme }) => theme.color.popup.input};

    &:focus {
      outline: none;
      border: 1px solid ${({ theme, $isError }) => ($isError ? theme.color.static.warning : theme.color.static.primary)};
    }
  }

  /* 기본 경로(SRightSideWithOnlyReset)의 .reset-btn(right:0; bottom:8px)이 emotion 삽입 순서상
     동일 specificity로 override를 덮어써 아이콘이 필드 밖으로 벗어나던 문제 → &&로 우선순위를 올려
     삽입 순서와 무관하게 항상 적용되도록 하고, 위치 규칙을 자기완결적으로 명시한다. */
  && .reset-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    bottom: auto;
    width: 20px;
    height: 20px;
    transform: translateY(-50%);
  }
`;

type SValidationSentenceProps = {
  $isError: boolean;
};
const SValidationSentence = styled.ul<SValidationSentenceProps>`
  font-size: 12px;
  color: ${({ theme, $isError }) => ($isError ? theme.color.static.warning : theme.color.text.TXT_1)};
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  padding-left: 12px;
`;

const SWebModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  width: 100%;

  button {
    display: flex;
    height: 46px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
  button:nth-of-type(1) {
    width: 120px;
    min-width: 120px;
    color: ${({ theme }) => theme.color.text.TXT_1};
    border: 1px solid ${({ theme }) => theme.color.buttons.disable};
  }

  button:nth-of-type(2) {
    width: 190px;
    font-weight: 500;
    background-color: ${({ theme }) => theme.color.static.primary};
    color: ${({ theme }) => theme.color.label.onAccent};

    &:disabled {
      background-color: ${({ theme }) => theme.color.buttons.disable};
      color: ${({ theme }) => theme.color.label.primary};
      font-weight: 400;
    }
  }
`;
